import { Typography, Tooltip, type TypographyProps } from '@mui/material';
import { useTimezone } from '@/hooks/use-timezone';
import { formatRelativeTime, isRecent } from '@/lib/timezone';

interface FormattedTimeProps extends Omit<TypographyProps, 'children' | 'variant'> {
  /** UTC ISO 8601 timestamp string */
  value?: string;
  /** Display mode: 'absolute' | 'relative' | 'auto' (default: 'auto') */
  variant?: 'absolute' | 'relative' | 'auto';
  /** Overrides the default Intl.DateTimeFormat options (absolute mode). */
  timeOptions?: Intl.DateTimeFormatOptions;
}

/**
 * Timezone-aware timestamp display component.
 * Reads the user's timezone from auth store via useTimezone().
 * Shows UTC value on hover tooltip.
 */
export function FormattedTime({ value, variant = 'auto', timeOptions, ...typographyProps }: FormattedTimeProps) {
  const { formatTime } = useTimezone();

  if (!value) {
    return (
      <Typography component="span" {...typographyProps}>
        Never
      </Typography>
    );
  }

  const showRelative =
    variant === 'relative' || (variant === 'auto' && isRecent(value));

  const relativeText = showRelative ? formatRelativeTime(value) : '';
  const displayText = relativeText || formatTime(value, timeOptions);

  // Show readable UTC time in tooltip
  const utcDate = new Date(value);
  const utcLabel = !isNaN(utcDate.getTime())
    ? `UTC: ${utcDate.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC')}`
    : value;

  return (
    <Tooltip title={utcLabel} arrow placement="top">
      <Typography component="span" {...typographyProps}>
        {displayText}
      </Typography>
    </Tooltip>
  );
}
